import { type SortRequest } from '@blaze-shared/api-contracts-domain';
import {
  type ImageBehavior as EngineImageBehavior,
  type ImageShape as EngineImageShape,
  type ListDirectionMode,
  type ListLayoutMode,
  ListScrollMode,
  type PropertiesConfigInputAlignModel,
} from '@blaze-shared/elements-domain';

import { ImageBehavior as AiImageBehavior, type EmptyImageState } from '../../../ai/models/image';
import { type ImageEmptyThumbnail } from '../../image/image.models';

import {
  type IconIndicatorAlign,
  ListBehavior,
  ListImageShape,
  ListMobileBehavior,
  ListSortDirection,
  type ListTemplate,
  type SortByEntry,
} from './list.definition';

export function toEngineListDirection(kind: ListTemplate['$type']): ListDirectionMode {
  switch (kind) {
    case 'vertical':
      return 'vertical';
    case 'horizontal':
      return 'horizontal';
    case 'ecommerce':
      return 'e-commerce';
    case 'subscription':
      return 'subscription-plan';
    case 'notification':
      return 'notification';
    case 'html':
      return 'custom';
  }
}

export function toListTemplateKind(direction: ListDirectionMode): ListTemplate['$type'] {
  switch (direction) {
    case 'vertical':
      return 'vertical';
    case 'horizontal':
      return 'horizontal';
    case 'e-commerce':
      return 'ecommerce';
    case 'subscription-plan':
      return 'subscription';
    case 'notification':
      return 'notification';
    case 'custom':
      return 'html';
  }
}

export function toEngineListLayoutMode(behavior: ListBehavior): ListLayoutMode {
  switch (behavior) {
    case ListBehavior.FixedList:
      return 'fixed';
    case ListBehavior.FullList:
      return 'full';
    case ListBehavior.HorizontalScrolling:
      return 'hscroll';
    case ListBehavior.VerticalScrolling:
      return 'vscroll';
    case ListBehavior.Paginate:
      return 'paginate';
  }
}

export function toListBehavior(mode: ListLayoutMode): ListBehavior | undefined {
  switch (mode) {
    case 'fixed':
      return ListBehavior.FixedList;
    case 'full':
      return ListBehavior.FullList;
    case 'hscroll':
      return ListBehavior.HorizontalScrolling;
    case 'vscroll':
      return ListBehavior.VerticalScrolling;
    case 'paginate':
      return ListBehavior.Paginate;
  }
}

export function toEngineListScrollMode(behavior: ListMobileBehavior): ListScrollMode {
  switch (behavior) {
    case ListMobileBehavior.Swipe:
      return ListScrollMode.Swipe;
    case ListMobileBehavior.Scroll:
      return ListScrollMode.Scroll;
  }
}

export function toListMobileBehavior(mode: ListScrollMode): ListMobileBehavior {
  switch (mode) {
    case ListScrollMode.Swipe:
      return ListMobileBehavior.Swipe;
    case ListScrollMode.Scroll:
      return ListMobileBehavior.Scroll;
  }
}

export function toEngineImageShape(shape: ListImageShape): EngineImageShape {
  switch (shape) {
    case ListImageShape.Square:
      return 'square';
    case ListImageShape.Circle:
      return 'circle';
    case ListImageShape.Landscape:
      return 'landscape';
    case ListImageShape.LandscapeShort:
      return 'landscape-short';
    case ListImageShape.Portrait:
      return 'portrait';
    case ListImageShape.PortraitShort:
      return 'portrait-short';
  }
}

export function toListImageShape(shape: EngineImageShape): ListImageShape | undefined {
  switch (shape) {
    case 'square':
      return ListImageShape.Square;
    case 'circle':
      return ListImageShape.Circle;
    case 'landscape':
      return ListImageShape.Landscape;
    case 'landscape-short':
      return ListImageShape.LandscapeShort;
    case 'portrait':
      return ListImageShape.Portrait;
    case 'portrait-short':
      return ListImageShape.PortraitShort;
    case 'auto':
      return undefined;
  }
}

export function toEngineImageBehavior(behavior: AiImageBehavior): EngineImageBehavior {
  return behavior === AiImageBehavior.Cover ? 'cover' : 'contain';
}

export function toListImageBehavior(behavior: EngineImageBehavior): AiImageBehavior {
  return behavior === 'cover' ? AiImageBehavior.Cover : AiImageBehavior.Fit;
}

export function toEngineEmptyImageThumbnail(state: EmptyImageState): ImageEmptyThumbnail {
  if (state.$type === 'icon') {
    return state.iconName.toLowerCase().includes('avatar') ? 'avatar-1' : 'image-1';
  }

  return 'custom-text';
}

export function toListEmptyImageState(thumbnail: ImageEmptyThumbnail): EmptyImageState {
  return thumbnail === 'custom-text' ? { $type: 'text' } : { $type: 'icon', iconName: thumbnail };
}

export function toEngineIndicatorAlign(align: IconIndicatorAlign): PropertiesConfigInputAlignModel {
  const result: PropertiesConfigInputAlignModel = {};

  if (align.horizontal) {
    result.horizontal = align.horizontal === 'center' ? 'right' : align.horizontal;
  }

  if (align.vertical) {
    result.vertical = align.vertical === 'center' ? 'middle' : align.vertical;
  }

  return result;
}

export function toIconIndicatorAlign(
  align: PropertiesConfigInputAlignModel | undefined,
): IconIndicatorAlign | undefined {
  if (!align) {
    return undefined;
  }

  const result: IconIndicatorAlign = {};

  if (
    align.horizontal === 'left' ||
    align.horizontal === 'right' ||
    align.horizontal === 'center'
  ) {
    result.horizontal = align.horizontal;
  }

  if (align.vertical) {
    result.vertical = align.vertical === 'middle' ? 'center' : align.vertical;
  }

  return result;
}

export function toEngineSortRequest(entry: SortByEntry): SortRequest {
  return {
    columnName: entry.field,
    direction: entry.direction === ListSortDirection.Descending ? 'Descending' : 'Ascending',
  };
}

export function toSortByEntry(request: SortRequest): SortByEntry | undefined {
  if (!request.columnName) {
    return undefined;
  }

  return {
    field: request.columnName,
    direction:
      request.direction === 'Descending'
        ? ListSortDirection.Descending
        : ListSortDirection.Ascending,
  };
}
